import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = () => api.get('/users');
export const updateSignature = (userId, signatureLogo) => 
  api.put(`/users/${userId}/signature`, null, { params: { signature_logo: signatureLogo } });

// Emails
export const getEmails = (status, search, limit = 200) => api.get('/emails', { params: { status, search, limit } });
export const syncEmailsNow = () => api.post('/emails/sync-now', {}, { timeout: 120000 }); // 2 min timeout for sync
export const getEmail = (id) => api.get(`/emails/${id}`);
export const createEmail = (data) => api.post('/emails', data);
export const analyzeEmail = (id) => api.post(`/emails/${id}/analyze`);
export const assignEmail = (emailId, userId) => 
  api.put(`/emails/${emailId}/assign`, null, { params: { user_id: userId } });

// Files
export const uploadFile = (file, emailId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (emailId) formData.append('email_id', emailId);
  return api.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Suppliers
export const getSuppliers = () => api.get('/suppliers');
export const createSupplier = (data) => api.post('/suppliers', data);
export const searchSuppliers = (category, specifications, location) =>
  api.post('/suppliers/search', null, { params: { category, specifications, location } });

// Quotes
export const getQuotes = (status) => api.get('/quotes', { params: { status } });
export const createQuote = (data) => api.post('/quotes', null, { params: data });
export const addSupplierQuote = (quoteId, data) => api.post(`/quotes/${quoteId}/supplier-quote`, data);
export const compareQuotes = (quoteId) => api.post(`/quotes/${quoteId}/compare`);
export const selectSupplier = (quoteId, supplierId) =>
  api.put(`/quotes/${quoteId}/select-supplier`, null, { params: { supplier_id: supplierId } });

// ERP Quotations (New Full Quotations)
export const getERPQuotations = (status) => api.get('/erp/quotations', { params: { status } });
export const getERPQuotation = (id) => api.get(`/erp/quotations/${id}`);
export const createERPQuotation = (data) => api.post('/erp/quotations', data);
export const updateERPQuotationStatus = (id, status) => 
  api.put(`/erp/quotations/${id}/status`, null, { params: { status } });
export const createDraftEmailFromQuotation = (id, customMessage) => 
  api.post(`/erp/quotations/${id}/create-draft-email`, null, { params: { custom_message: customMessage } });
export const getERPCustomers = () => api.get('/erp/customers');
export const getERPSuppliers = () => api.get('/erp/suppliers');
export const getERPProjects = () => api.get('/erp/projects');

// Approvals
export const getApprovals = (status) => api.get('/approvals', { params: { status } });
export const createApproval = (data) => api.post('/approvals', data);
export const approveRequest = (id, comments) =>
  api.put(`/approvals/${id}/approve`, null, { params: { comments } });
export const rejectRequest = (id, comments) =>
  api.put(`/approvals/${id}/reject`, null, { params: { comments } });
export const deleteApproval = (id) => api.delete(`/approvals/${id}`);

// Drafts
export const getDrafts = () => api.get('/drafts');
export const generateDraft = (emailId, responseType, additionalContext) =>
  api.post('/drafts/generate', null, { params: { email_id: emailId, response_type: responseType, additional_context: additionalContext } });
export const updateDraft = (id, data) => api.put(`/drafts/${id}`, null, { params: data });
export const submitDraftForApproval = (id) => api.post(`/drafts/${id}/submit-for-approval`);
export const sendDraft = (id) => api.post(`/drafts/${id}/send`);
export const deleteDraft = (id) => api.delete(`/drafts/${id}`);

// Chat
export const sendChatMessage = (message, emailId, sessionId, fileContent, fileName, filePaths) =>
  api.post('/chat', { 
    message, 
    email_id: emailId, 
    session_id: sessionId, 
    file_content: fileContent, 
    file_name: fileName,
    file_paths: filePaths || []
  });

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Email Settings - Multiple Accounts
export const connectEmail = (settings) => api.post('/settings/email/connect', settings);
export const getEmailSettings = () => api.get('/settings/email');
export const getEmailAccount = (accountId) => api.get(`/settings/email/${accountId}`);
export const setEmailPrimary = (accountId) => api.put(`/settings/email/${accountId}/primary`);
export const deleteEmailAccount = (accountId) => api.delete(`/settings/email/${accountId}`);
export const disconnectEmail = () => api.delete('/settings/email/disconnect');
export const syncEmails = () => api.post('/settings/email/sync');
export const sendEmailViaAccount = (emailData) => api.post('/settings/email/send', emailData);
export const analyzeEmailAttachments = (emailId) => api.post(`/emails/${emailId}/analyze-attachments`);
export const resyncEmailAttachments = (emailId) => api.post(`/emails/${emailId}/resync-attachments`);
export const getEmailLinks = (emailId) => api.get(`/emails/${emailId}/links`);
export const downloadEmailLinks = (emailId) => api.post(`/emails/${emailId}/download-links`);
export const sendEmailFromChat = (emailData) => api.post('/chat/send-email', emailData);

// PDF Modifier - Comprehensive
export const addLogoToPdf = (formData) => 
  api.post('/pdf/add-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const replaceLogoInPdf = (formData) => 
  api.post('/pdf/replace-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const addHeaderFooterToPdf = (formData) => 
  api.post('/pdf/header-footer', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const addWatermarkToPdf = (formData) => 
  api.post('/pdf/watermark', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const addStampToPdf = (formData) => 
  api.post('/pdf/stamp', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const mergePdfs = (formData) => 
  api.post('/pdf/merge', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const reorderPdfPages = (formData) => 
  api.post('/pdf/reorder', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deletePdfPages = (formData) => 
  api.post('/pdf/delete-pages', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const splitPdf = (formData) => 
  api.post('/pdf/split', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const convertToDhrFormat = (formData) => 
  api.post('/pdf/convert-to-dhr', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const replaceTextInPdf = (formData) => 
  api.post('/pdf/replace-text', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const createBusinessCard = (formData) => 
  api.post('/pdf/create-business-card', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getPdfInfo = (formData) => 
  api.post('/pdf/info', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const downloadModifiedPdf = (filename) => 
  `${API_URL}/api/pdf/download/${filename}`;

export default api;

// PDF Text Extraction and Inline Editing
export const extractPdfText = (formData) => 
  api.post('/pdf/extract-text', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const editPdfTextInline = (formData) => 
  api.post('/pdf/edit-text-inline', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const renderPdfPreview = (formData) => 
  api.post('/pdf/render-preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getPdfPreviewUrl = (filename) => 
  `${API_URL}/api/pdf/preview/${filename}`;

// Quantity Calculator
export const calculateQuantities = (buildingData, projectInfo, selectedCategories) =>
  api.post('/quantity/calculate', { buildingData, projectInfo, selectedCategories });
export const calculateQuantitiesSimple = (formData) =>
  api.post('/quantity/calculate-simple', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getQuantityCategories = () => api.get('/quantity/categories');
export const uploadProjectFile = (file, projectName) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project_name', projectName);
  return api.post('/quantity/upload-project', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    maxContentLength: Infinity,
    maxBodyLength: Infinity
  });
};

// Quantity Report Export
export const exportQuantityExcel = (buildingData, projectInfo, selectedCategories) =>
  api.post('/quantity/export/excel', { buildingData, projectInfo, selectedCategories });
export const exportQuantityPdf = (buildingData, projectInfo, selectedCategories) =>
  api.post('/quantity/export/pdf', { buildingData, projectInfo, selectedCategories });
export const getQuantityDownloadUrl = (filename) => 
  `${API_URL}/api/quantity/download/${filename}`;

// File Analysis
export const analyzeProjectFile = (filePath) => {
  const formData = new FormData();
  formData.append('file_path', filePath);
  return api.post('/quantity/analyze-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const analyzeAndCalculate = (file, projectName) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project_name', projectName);
  return api.post('/quantity/analyze-and-calculate', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    timeout: 120000  // 2 minutes for AI analysis
  });
};

// CAD Generation
export const generateCadDrawings = (buildingData, projectInfo, selectedCategories) =>
  api.post('/quantity/generate-cad', { buildingData, projectInfo, selectedCategories });
export const generateSingleCad = (width, length, height, planType, floorName) => {
  const formData = new FormData();
  formData.append('width', width);
  formData.append('length', length);
  formData.append('height', height);
  formData.append('plan_type', planType);
  formData.append('floor_name', floorName);
  return api.post('/quantity/generate-single-cad', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Project Management
export const listQuantityProjects = (userId = null, status = null, limit = 50) => {
  const params = new URLSearchParams();
  if (userId) params.append('user_id', userId);
  if (status) params.append('status', status);
  params.append('limit', limit);
  return api.get(`/quantity/projects?${params.toString()}`);
};
export const getQuantityProject = (projectId) => api.get(`/quantity/projects/${projectId}`);
export const createQuantityProject = (project) => api.post('/quantity/projects', project);
export const updateQuantityProject = (projectId, updates) => api.put(`/quantity/projects/${projectId}`, updates);
export const deleteQuantityProject = (projectId) => api.delete(`/quantity/projects/${projectId}`);
export const calculateAndSaveProject = (projectId) => api.post(`/quantity/projects/${projectId}/calculate`);
export const saveCurrentProject = (name, description, userId, dimensions, floors, projectInfo, quantities) => {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('description', description || '');
  formData.append('user_id', userId || 'anonymous');
  formData.append('dimensions', JSON.stringify(dimensions || {}));
  formData.append('floors', JSON.stringify(floors || []));
  formData.append('project_info', JSON.stringify(projectInfo || {}));
  formData.append('quantities', JSON.stringify(quantities || {}));
  return api.post('/quantity/projects/save-current', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
